import { useEffect, useId, useRef, useState } from 'react';
import { addableGroups, type SymbolLike } from './marketWatchModel';

/** A modal list of the symbols that are not in the Market Watch yet, searchable and grouped by category. */
export function AddSymbolDialog({
  open,
  available,
  watched,
  onAdd,
  onClose,
}: {
  open: boolean;
  available: SymbolLike[];
  watched: string[];
  onAdd: (name: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const searchId = useId();
  const [search, setSearch] = useState('');
  const groups = addableGroups(available, watched, search);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={`${searchId}-title`}
      onClose={onClose}
      onCancel={onClose}
      className="m-auto w-full max-w-md rounded-[var(--radius)] border border-line bg-panel p-0 text-fg shadow-2xl backdrop:bg-black/60"
    >
      <div className="flex max-h-[70vh] flex-col p-4">
        <h2 id={`${searchId}-title`} className="mb-3 text-base font-semibold">
          Add symbols
        </h2>
        <label htmlFor={searchId} className="sr-only">
          Search symbols
        </label>
        <input
          id={searchId}
          type="search"
          autoFocus
          placeholder="Search by name or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            const first = groups[0]?.symbols[0];
            if (e.key === 'Enter' && first) onAdd(first.name);
          }}
          className="mb-3 w-full rounded-md border border-line bg-panel2 px-3 py-2 outline-none focus:border-accent"
        />
        <div className="min-h-0 flex-1 overflow-auto" data-testid="add-symbol-list">
          {groups.length === 0 && <p className="p-2 text-sm text-muted">No symbols match.</p>}
          {groups.map((group) => (
            <section key={group.category} aria-label={group.category} className="mb-3">
              <h3 className="sticky top-0 bg-panel py-1 text-xs uppercase tracking-wide text-muted">
                {group.category}
              </h3>
              <ul>
                {group.symbols.map((s) => (
                  <li key={s.name}>
                    <button
                      type="button"
                      onClick={() => onAdd(s.name)}
                      className="flex w-full items-baseline justify-between rounded-md px-2 py-1.5 text-left hover:bg-panel2 focus-visible:bg-panel2"
                    >
                      <span className="font-medium">{s.name}</span>
                      <span className="ml-3 truncate text-sm text-muted">{s.description}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-line bg-panel2 px-4 py-1.5 hover:border-accent"
          >
            Done
          </button>
        </div>
      </div>
    </dialog>
  );
}
